(() => {
  if (customElements.get('blvnks-size-run')) return;

  class BlvnksSizeRun extends HTMLElement {
    constructor() {
      super();
      this.rowsEl = this.querySelector('[data-size-rows]');
      this.totalUnitsEl = this.querySelector('[data-total-units]');
      this.totalPriceEl = this.querySelector('[data-total-price]');
      this.addButton = this.querySelector('[data-bulk-add]');
      this.addLabel = this.querySelector('[data-bulk-add-label]');
      this.spinner = this.querySelector('.loading__spinner');
      this.errorEl = this.querySelector('[data-error]');
      this.breakdownEl = this.querySelector('[data-breakdown]');
      this.designLink = this.querySelector('[data-design-link]');
      this.productionLink = this.querySelector('[data-production-link]');
      this.quantities = new Map();
      this.currentVariants = [];
      this.isSubmitting = false;

      try {
        this.data = JSON.parse(this.querySelector('[data-blvnks-product]')?.textContent || '{}');
      } catch (error) {
        console.error('[BLVNKS size run] Invalid product JSON', error);
        this.data = { options: [], variants: [] };
      }

      this.sizePosition = Number(this.dataset.sizePosition || 0);
      this.colorPosition = Number(this.dataset.colorPosition || 0);
      this.currency = this.dataset.currency || 'USD';

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onPickerChange = this.onPickerChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      this.pickerHost = this.closest('product-info') || document;
      this.pickerHost.addEventListener('change', this.onPickerChange);

      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.pickerHost?.removeEventListener('change', this.onPickerChange);
    }

    variantPicker() {
      return this.closest('product-info')?.querySelector('variant-selects') ||
        document.querySelector(`#variant-selects-${CSS.escape(this.dataset.sectionId)}`);
    }
    
    onPickerChange(event) {
      if (!event.target.closest?.('variant-selects')) return;
      requestAnimationFrame(() => this.render());
    }

    onVariantChange(event) {
      const detail = event?.data || event?.detail?.data || event?.detail || {};
      const sectionId = detail.sectionId;

      if (
        sectionId &&
        String(sectionId) !== String(this.dataset.sectionId)
      ) {
        return;
      }

      this.quantities.clear();

      requestAnimationFrame(() => {
        this.render();
      });
    }

    selectedOptionValue(optionName) {
      const picker = this.closest('product-info')?.querySelector('variant-selects') ||
        document.querySelector(`#variant-selects-${CSS.escape(this.dataset.sectionId)}`);

      if (!picker) return null;

      const candidates = picker.querySelectorAll(
        `input[data-option-name="${CSS.escape(optionName)}"]:checked, select[name="options[${CSS.escape(optionName)}]"]`
      );

      for (const node of candidates) {
        if (node.tagName === 'SELECT') return node.value;
        if (node.checked) return node.value;
      }

      return null;
    }

    optionAt(variant, position) {
      return variant[`option${position}`] ?? null;
    }

  getCurrentColor() {
    if (!this.colorPosition) return null;

    const picker = this.variantPicker();
    if (!picker) return null;

    const wanted = String(this.data.options?.[this.colorPosition - 1] || '').trim();

    for (const input of picker.querySelectorAll('input:checked')) {
      if ((input.dataset.optionName || '').trim() === wanted) return input.value;
    }

    for (const select of picker.querySelectorAll('select')) {
      if ((select.dataset.optionName || '').trim() === wanted) return select.value;
    }

    const group = picker.querySelectorAll('.product-form__input')[this.colorPosition - 1];
    if (group) {
      const checked = group.querySelector('input:checked');
      if (checked) return checked.value;
      const select = group.querySelector('select');
      if (select) return select.value;
    }

    return null;
  }

    variantsForCurrentSelection() {
      const variants = Array.isArray(this.data.variants) ? this.data.variants : [];
      const color = this.getCurrentColor();

      return variants.filter((variant) => {
        if (this.colorPosition && color) {
          if (this.optionAt(variant, this.colorPosition) !== color) return false;
        }

        for (let position = 1; position <= 3; position += 1) {
          if (position === this.sizePosition || position === this.colorPosition) continue;
          const name = this.data.options?.[position - 1];
          if (!name) continue;
          const selected = this.selectedOptionValue(name);
          if (selected && this.optionAt(variant, position) !== selected) return false;
        }

        return true;
      });
    }

    updateDesignLink() {
      if (!this.designLink) return;

      const handle = String(this.dataset.productHandle || '').trim();
      if (!handle) return;

      const params = new URLSearchParams();
      params.set('view', 'customize');

      const color = this.getCurrentColor();
      const variants = this.variantsForCurrentSelection();
      const selectedVariant = variants.find((variant) => variant.available) || variants[0];

      if (selectedVariant && selectedVariant.id) {
        params.set('variant', String(selectedVariant.id));
      } else if (color) {
        params.set('color', color);
      }

      this.designLink.href = `/products/${encodeURIComponent(handle)}?${params.toString()}`;
      this.updateProductionLink();
    }

    updateProductionLink() {
      if (!this.productionLink) return;

      const handle = String(this.dataset.productHandle || '').trim();
      if (!handle) return;

      const params = new URLSearchParams();
      params.set('product', handle);

      const color = this.getCurrentColor();
      if (color) params.set('color', color);

      this.productionLink.href = `/pages/production?${params.toString()}`;
    }

    sizeLabel(variant) {
      return this.optionAt(variant, this.sizePosition) || variant.title;
    }

    money(cents) {
      const value = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency,
          currencyDisplay: 'narrowSymbol'
        }).format(value);
      } catch {
        return `$${value.toFixed(2)}`;
      }
    }

    render() {
      this.currentVariants = this.variantsForCurrentSelection();
      this.updateDesignLink();

      if (!this.rowsEl) return;

      if (!this.currentVariants.length) {
        this.rowsEl.innerHTML =
          '<div class="blvnks-size-run__empty">No sizes are available for this selection.</div>';
        this.updateSummary();
        return;
      }

      this.rowsEl.innerHTML = this.currentVariants.map((variant) => {
        const quantity = this.quantities.get(String(variant.id)) || 0;
        const size = this.escapeHtml(this.sizeLabel(variant));
        const soldOut = !variant.available;

        return `
          <div class="blvnks-size-run__row${soldOut ? ' is-sold-out' : ''}" role="row" data-variant-row="${variant.id}">
            <span class="blvnks-size-run__size" role="cell">${size}</span>
            <span class="blvnks-size-run__price" role="cell">${this.money(variant.price)}</span>
            <div class="blvnks-size-run__qty" role="cell">
              ${
                soldOut
                  ? '<span class="blvnks-size-run__sold-out">Sold out</span>'
                  : `
                    <button type="button" class="blvnks-size-run__qty-btn" data-minus="${variant.id}" aria-label="Decrease ${size} quantity">−</button>
                    <input
                      class="blvnks-size-run__qty-input"
                      type="number"
                      min="0"
                      step="1"
                      inputmode="numeric"
                      value="${quantity}"
                      data-qty="${variant.id}"
                      aria-label="${size} quantity"
                    >
                    <button type="button" class="blvnks-size-run__qty-btn" data-plus="${variant.id}" aria-label="Increase ${size} quantity">+</button>
                  `
              }
            </div>
          </div>
        `;
      }).join('');

      this.updateSummary();
    }

    onClick(event) {
      const minus = event.target.closest('[data-minus]');
      const plus = event.target.closest('[data-plus]');

      if (minus) {
        this.adjust(minus.dataset.minus, -1);
        return;
      }

      if (plus) {
        this.adjust(plus.dataset.plus, 1);
        return;
      }

      if (event.target.closest('[data-bulk-add]')) {
        this.addSelectedToCart();
      }
    }

    onChange(event) {
      const input = event.target.closest('[data-qty]');
      if (!input) return;

      const id = String(input.dataset.qty);
      const value = Math.max(0, Math.floor(Number(input.value) || 0));
      input.value = value;
      this.quantities.set(id, value);
      this.updateSummary();
    }

    adjust(id, amount) {
      id = String(id);
      const input = this.querySelector(`[data-qty="${CSS.escape(id)}"]`);
      if (!input) return;

      const next = Math.max(0, (Number(input.value) || 0) + amount);
      input.value = next;
      this.quantities.set(id, next);
      this.updateSummary();
    }

    selectedItems() {
      const items = [];

      for (const variant of this.data.variants || []) {
        const quantity = this.quantities.get(String(variant.id)) || 0;
        if (quantity <= 0 || !variant.available) continue;

        items.push({
          id: variant.id,
          quantity,
          price: variant.price,
          color: this.colorPosition ? this.optionAt(variant, this.colorPosition) : null,
          size: this.sizeLabel(variant)
        });
      }

      return items;
    }

    updateSummary() {
      const items = this.selectedItems();
      const units = items.reduce((sum, item) => sum + item.quantity, 0);
      const total = items.reduce((sum, item) => sum + item.quantity * item.price, 0);

      if (this.totalUnitsEl) {
        this.totalUnitsEl.textContent = `${units} ${units === 1 ? 'unit' : 'units'}`;
      }

      if (this.totalPriceEl) {
        this.totalPriceEl.textContent = this.money(total);
      }

      if (this.addLabel) {
        this.addLabel.textContent =
          units > 0 ? `Add ${units} to cart — ${this.money(total)}` : 'Add to cart';
      }

     if (this.addButton) {
        this.addButton.disabled = units === 0 || this.isSubmitting;
      }

      this.renderBreakdown(items);
    }

    renderBreakdown(items) {
      if (!this.breakdownEl) return;

      const groups = new Map();

      for (const item of items) {
        const key = item.color || 'Selection';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(`${item.quantity} ${item.size}`);
      }

      this.breakdownEl.innerHTML = [...groups]
        .map(([color, parts]) =>
          `<li><span>${this.escapeHtml(color)}</span><span>${this.escapeHtml(parts.join(', '))}</span></li>`
        )
        .join('');

      this.breakdownEl.hidden = groups.size === 0;
    }

    async addSelectedToCart() {
      if (this.isSubmitting) return;

      const items = this.selectedItems().map(({ id, quantity }) => ({ id, quantity }));
      if (!items.length) return;

      this.setError('');
      this.setSubmitting(true);

      try {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const body = { items };

        if (cart?.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add these items to cart.');
        }

      window.location.href = window.routes?.cart_url || '/cart';
      return;
      } catch (error) {
        console.error('[BLVNKS size run]', error);
        this.setError(error.message || 'Unable to add these items to cart.');
      } finally {
        this.setSubmitting(false);
      }
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      this.addButton?.classList.toggle('loading', isSubmitting);
      this.spinner?.classList.toggle('hidden', !isSubmitting);
      this.updateSummary();
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = String(value ?? '');
      return div.innerHTML;
    }
  }

  customElements.define('blvnks-size-run', BlvnksSizeRun);
})();
